import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit, inject } from '@angular/core';
import { Location } from '@angular/common';

import { AuthService } from '../../services/auth/auth.service';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

import { CaptureComponent } from '../../components/capture/capture.component';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { BannerComponent } from '../../components/banner/banner.component';
import { Bind } from 'primeng/bind';
import { Toast } from 'primeng/toast';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Button } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-printer',
  templateUrl: './printer.component.html',
  styleUrls: ['./printer.component.scss'],
  imports: [
    BannerComponent,
    CaptureComponent,
    Bind,
    Toast,
    PrimeTemplate,
    ConfirmDialog,
    Button,
    TranslatePipe
  ]
})
export class PrinterComponent implements OnInit, OnDestroy, AfterViewInit {
  private _location = inject(Location);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private tenantService = inject(TenantApiService);
  private spinner = inject(NgxSpinnerService);
  private whatFix = inject(WhatFixService);

  @ViewChild(CaptureComponent) captureComponent!: CaptureComponent;

  private subscription = new Subscription();

  autoclassificationFlag$ = new BehaviorSubject<boolean>(false);

  tenantId: string = '';
  documentIds: string[] = [];

  authToken: string = '';

  isWebApps = false;

  constructor() {
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        this.documentIds = params['id'].split(',');
        this.authToken = params['accessToken'];
      })
    );
  }

  /**
   * Initializes the component, setting up necessary data and feature flags
   */
  ngOnInit() {
    document.body.classList.add('overflow-scroll-issue');
    const { key: userId, tenantId } = this.authService.extractUserInfo(this.authToken);
    this.tenantId = tenantId;
    localStorage.setItem('tenantId', tenantId);
    this.tenantService.getTenantUrls(this.tenantId).subscribe(tenantUrls => {
      localStorage.setItem('contentURL', tenantUrls.contentUrl);
    });

    this.whatFix.initializeWithContext(this.tenantId);

    this.featureFlagService.initialize({ userId: userId, tenantId });
  }

  /**
   * After the view is initialized, focus the search input.
   * Uses a microtask to avoid ExpressionChangedAfterItHasBeenCheckedError.
   */
  ngAfterViewInit() {
    if (this.authToken) {
      this.authService.setAuthToken(this.authToken);
    }
    if (this.captureComponent && this.documentIds.length > 0) {
      this.captureComponent.startAdHocFiling(this.documentIds, this.authToken);
    }
  }

  /**
   * Redirects the user back to the printer application
   */
  redirectBack(): void {
    setTimeout(() => {
      this._location.back();
    }, 2000); // Wait 2 seconds before navigating back
  }

  /**
   * Angular lifecycle hook.
   * Called once when the component is destroyed.
   * Releases subscriptions, timers, and listeners to prevent memory leaks.
   */
  ngOnDestroy(): void {
    document.body.classList.remove('overflow-scroll-issue');
    localStorage.removeItem('vanityName');
    localStorage.removeItem('tenantId');
    this.subscription.unsubscribe();
  }

  /**
   * Shows an error toast message.
   * @param {string} toastMessage - The error message to display.
   * @param {string} [toastData] - Additional data to display in the toast.
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData ? toastData : '',
      life: 10000
    });
  }
}
